import { getStoreSettingsForKiosk, getStoreLocationForKiosk } from '../EcommerceService.js';

export function extractEcomApiRecord(response) {
  if (response?.status !== 'ok') {
    return null;
  }
  const { result } = response;
  if (!result || typeof result !== 'object') {
    return null;
  }
  if (Array.isArray(result)) {
    return result[0] ?? null;
  }
  return result;
}

export function mapLocationToLegacyShippingAddress(location) {
  if (!location) {
    return {};
  }

  return {
    shopifyLocationName: location.location_name ?? '',
    shopifyLocationExternalId: location.location_external_id ?? '',
    shopifyFirstName: location.first_name ?? '',
    shopifyLastName: location.last_name ?? '',
    shopifyAddress1: location.address1 ?? '',
    shopifyAddress2: location.address2 ?? '',
    shopifyCountry: location.country ?? '',
    shopifyState: location.state ?? '',
    shopifyCity: location.city ?? '',
    shopifyZip: location.zip ?? '',
    shopifyCompany: location.company ?? '',
    shopifyPhoneNumber: location.phone ?? '',
  };
}

export function isTruthyEcomFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function toEcomStorageOneOrZero(value) {
  return isTruthyEcomFlag(value) ? 1 : 0;
}

export function parseLegacyShippingAddress(legacyShippingAddress) {
  if (legacyShippingAddress == null || legacyShippingAddress === 'null') {
    return {};
  }

  if (typeof legacyShippingAddress === 'string') {
    try {
      const parsed = JSON.parse(legacyShippingAddress);
      if (parsed && typeof parsed === 'object') {
        return parsed.shopifyAddress1 != null
          ? parsed
          : mapLocationToLegacyShippingAddress(parsed);
      }
    } catch {
      return {};
    }
    return {};
  }

  if (typeof legacyShippingAddress === 'object') {
    return legacyShippingAddress.shopifyAddress1 != null
      ? legacyShippingAddress
      : mapLocationToLegacyShippingAddress(legacyShippingAddress);
  }

  return {};
}

export async function loadEcommerceCheckoutSettings({
  eventName,
  eventPrivateApiKey,
  legacyOpts = {},
  legacyShippingAddress = null,
}) {
  let storeSettings = null;
  let storeLocation = null;

  if (eventName && eventPrivateApiKey) {
    try {
      const settingsResponse = await getStoreSettingsForKiosk({ eventName, eventPrivateApiKey });
      storeSettings = extractEcomApiRecord(settingsResponse);
    } catch (error) {
      console.warn('Failed to load ecommerce settings:', error);
    }

    try {
      const locationResponse = await getStoreLocationForKiosk({ eventName, eventPrivateApiKey });
      storeLocation = extractEcomApiRecord(locationResponse);
    } catch (error) {
      console.warn('Failed to load ecommerce location:', error);
    }
  }

  const shopDomain = storeSettings?.shop_domain?.trim() || legacyOpts?.shopDomain?.trim() || '';
  const shopToken = storeSettings?.shop_token?.trim() || legacyOpts?.shopToken?.trim() || '';
  const isUpsellProductsCheck =
    storeSettings != null
      ? toEcomStorageOneOrZero(storeSettings.is_upsell_products_check)
      : toEcomStorageOneOrZero(legacyOpts?.isUpsellProductsCheck);
  const hideProductPrice =
    storeSettings != null
      ? toEcomStorageOneOrZero(storeSettings.hide_product_price)
      : toEcomStorageOneOrZero(legacyOpts?.hideProductPrice);
  const enabledShopComboRequired =
    storeSettings != null
      ? isTruthyEcomFlag(storeSettings.enabled_shop_combo_required)
      : isTruthyEcomFlag(legacyOpts?.enabledShopComboRequired);
  const shippingAddress = storeLocation
    ? mapLocationToLegacyShippingAddress(storeLocation)
    : parseLegacyShippingAddress(legacyShippingAddress);

  return {
    shopDomain,
    shopToken,
    isUpsellProductsCheck,
    hideProductPrice,
    enabledShopComboRequired,
    shippingAddress,
    mergedOpts: {
      ...legacyOpts,
      shopDomain,
      shopToken,
      isUpsellProductsCheck,
      hideProductPrice,
      enabledShopComboRequired,
    },
  };
}

export function applyEcommerceCheckoutSettingsToStorage(settings, storage = localStorage) {
  storage.setItem('eventOpts', JSON.stringify(settings.mergedOpts));
  storage.setItem('isUpsellProductsCheck', settings.isUpsellProductsCheck);
  storage.setItem('enabledShopComboRequired', settings.enabledShopComboRequired);

  if (settings.hideProductPrice) {
    storage.setItem('hideProductPrice', settings.hideProductPrice);
  } else {
    storage.removeItem('hideProductPrice');
  }

  if (settings.shopDomain) {
    storage.setItem('shopDomain', settings.shopDomain);
  }
  if (settings.shopToken) {
    storage.setItem('shopToken', settings.shopToken);
  }

  storage.setItem('shippingAddress', JSON.stringify(settings.shippingAddress));
}
